// src/admin/pages/ContextSettingsPage.jsx
// Superadmin console → «Configurazione tenant» (tab row; no sidebar entry since v219). Route /admin/context-settings.
// SAs have no implicit tenant → tenant dropdown (mirrors KnowledgeBasePage), URL-synced,
// last-used tenant remembered per-user in localStorage.
import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { adminListTenants } from '../adminApi.js';
import { useAuth } from '../../auth/AuthContext.jsx';
import ContextConfigView from '../../context/ContextConfigView.jsx';
import { COLORS, FONT_SANS, RADII } from '../../constants/theme.js';

const FONT = FONT_SANS;

export default function ContextSettingsPage() {
  const { t } = useTranslation('admin');
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlTenant = searchParams.get('tenant') || '';
  const [tenants, setTenants] = useState([]);

  const tenantStorageKey = `ctxconfig_last_tenant:${user?.user_id || user?.email || 'anon'}`;

  const patchParams = useCallback((patch) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([k, v]) => {
      if (v === '' || v == null) next.delete(k);
      else next.set(k, String(v));
    });
    setSearchParams(next, { replace: false });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    adminListTenants().then(setTenants).catch(() => setTenants([]));
  }, []);

  useEffect(() => {
    if (urlTenant) {
      try { localStorage.setItem(tenantStorageKey, urlTenant); } catch {}
      return;
    }
    try {
      const last = localStorage.getItem(tenantStorageKey);
      if (last) patchParams({ tenant: last });
    } catch {}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlTenant, tenantStorageKey]);

  return (
    <div style={{ padding: '32px 40px', fontFamily: FONT, color: COLORS.ink, maxWidth: '1100px' }}>
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontSize: '22px', fontWeight: 700, margin: 0 }}>{t('contextSettings.title')}</h1>
        <p style={{ color: COLORS.textSecondary, fontSize: '13px', margin: '4px 0 0' }}>
          {t('contextSettings.subtitle')}
        </p>
      </div>

      <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
        <label style={{ fontSize: '13px', color: COLORS.ink }}>{t('shared.tenantLabel')}</label>
        <select
          value={urlTenant}
          onChange={e => patchParams({ tenant: e.target.value })}
          style={{
            padding: '6px 10px', borderRadius: RADII.controlInner,
            border: `1px solid ${COLORS.borderStrong}`, background: COLORS.surface,
            fontSize: '13px', fontFamily: FONT, minWidth: '240px',
          }}
        >
          <option value="">{t('shared.selectPlaceholder')}</option>
          {tenants.map(tn => (
            <option key={tn.id} value={tn.id}>{tn.display_name || tn.id}</option>
          ))}
        </select>
      </div>

      {urlTenant ? (
        <ContextConfigView tenantId={urlTenant} canWrite />
      ) : (
        <div style={{
          padding: '60px 40px', textAlign: 'center', background: COLORS.surface,
          borderRadius: RADII.control, border: `1px solid ${COLORS.border}`,
          color: COLORS.textSecondary, fontSize: '13px',
        }}>
          {t('contextSettings.selectTenantPrompt')}
        </div>
      )}
    </div>
  );
}
